import { useContext, useEffect } from 'react'
//Providers - Context
import { I18nContext } from '../context/I18nProvider'
import { ItinerariesContext } from '../context/ItinerariesProvider'
//Libs
import { useParams, Redirect, useHistory } from 'react-router-dom'
import { useQuery } from 'react-query'
import Translate from '../i18n/Translate'
import { useTranslation } from 'react-i18next'
//API
import Api from '../api'
//Helpers
import { chooseUrlByOrientationAndPixelRatio, isEmptyObject } from '../helpers'
//Components
import InfoGeneral from '../components/ItineraryDetail/InfoGeneral'
import SimilarPackages from '../containers/ItineraryDetail/SimilarPackages'
import OperatorsGallery from '../components/OperatorsGallery'
import ReservationButton from '../components/ItineraryDetail/ReservationButton'
import PageLoader from '../components/PageLoader'
import DayDetail from '../components/ItineraryDetail/DayDetail'
import NotFound from './NotFound'
//Containers
import Reservations from '../containers/ItineraryDetail/Reservations'
//Intersection
import { useInView } from 'react-intersection-observer'
//Hooks
import useWindowWidth from '../hooks/useWindowWidth'
import useScrollTop from '../hooks/useScrollTop'
import useDevicePixelRatio from '../hooks/useDevicePixelRatio'
import useScreenOrientation from '../hooks/useScreenOrientation'
//Placeholders
import placeholder_768 from '../assets/images/placeholders/placeholder_768x768.jpg'
import placeholder_1920x768 from '../assets/images/placeholders/placeholder_1920x768.jpg'
import useSetI18nProviderInformation from '../hooks/useSetI18nProviderInformation'

const ItineraryDetail = () => {
    const { slug } = useParams()

    useScrollTop()
    useSetI18nProviderInformation('itinerary_detail', slug)

    const windowWidth = useWindowWidth()
    const devicePixelRatio = useDevicePixelRatio()
    const screenOrientation = useScreenOrientation()

    const history = useHistory()

    const { t } = useTranslation()

    const { language, path } = useContext(I18nContext)
    const { selectedItinerarieID, setSelectedItinerarieID } = useContext(ItinerariesContext)

    //Le pasamos el slug para obtener el itinerario especifico
    const { data: itinerary, status } = useQuery(['single-itinerary', slug, language], () =>
        Api.itineraries.single(slug, selectedItinerarieID, language)
    )

    useEffect(() => {
        itinerary && setSelectedItinerarieID(itinerary.id)
    }, [itinerary])

    //Reemplazamos la url por la que corresponda segun el lenguaje.
    //Cambia el path de la url y slug del itinerario
    useEffect(() => {
        if (itinerary && !isEmptyObject(itinerary) && itinerary.slug !== slug) {
            history.replace(`/${language}${path}/${itinerary.slug}`)
        }
    }, [language, itinerary])

    const [detailTop, inView] = useInView({
        triggerOnce: true,
        threshold: 0.5,
    })

    const [downloadBtn, inView2] = useInView({
        triggerOnce: true,
        threshold: 0.5,
    })

    const backgroundImage = () => {
        const result = chooseUrlByOrientationAndPixelRatio(screenOrientation, devicePixelRatio, itinerary.images)

        if (result === 'placeholder_portrait') {
            return {
                backgroundImage: `url(${placeholder_768})`,
            }
        } else if (result === 'placeholder_landscape') {
            return {
                backgroundImage: `url(${placeholder_1920x768})`,
            }
        } else {
            return {
                backgroundImage: `url(${result})`,
            }
        }
    }

    //Mientras carga
    if (status === 'loading') {
        return <PageLoader />
    }
    //Si hubo error al hacer el fetch
    if (status === 'error') {
        return <Redirect to={`/${language}${t('routes.home')}`} />
    }
    //Si no se encontro itinerario con ese ID
    if (!itinerary || isEmptyObject(itinerary)) {
        return <NotFound />
    }

    return (
        <main className="incoming-itinerary-detail">
            <section className="incoming-itinerary-detail__top" style={backgroundImage()}>
                <div className={`content animate slide-title ${inView ? 'in-view' : ''}`} ref={detailTop}>
                    <h1 className="title top-title">{itinerary.title}</h1>
                    <p className="copy top-subtitle">{itinerary.places.join(' - ')}</p>
                    <p className="days top-subtitle">
                        {itinerary.days}{' '}
                        {itinerary.days > 1 ? (
                            <Translate langkey="commons.days" />
                        ) : (
                            <Translate langkey="commons.day" />
                        )}{' '}
                        / {itinerary.nights}{' '}
                        {itinerary.nights > 1 ? (
                            <Translate langkey="commons.nights" />
                        ) : (
                            <Translate langkey="commons.night" />
                        )}
                    </p>
                </div>
                <a
                    href={itinerary.pdf_link}
                    className={`download-btn animate slide-up ${inView2 ? 'in-view' : ''}`}
                    ref={downloadBtn}
                    target="_blank"
                >
                    <span>
                        <Translate langkey="itinerary_detail.download" />
                    </span>
                </a>
            </section>

            <div className="incoming-itinerary-detail__info-container">
                <InfoGeneral info={itinerary.general_info} />

                {/* Itinerarios puede ser una seccion con varios dias, o varias secciones con varios dias */}
                {itinerary.itineraries ? (
                    <article className="itinerary">
                        {itinerary.itineraries.map(({ subtitle, text, itinerary }, index) => {
                            return (
                                <section key={index}>
                                    {subtitle && <h2 className="itinerary__title">{subtitle}</h2>}
                                    {text && (
                                        <div
                                            className="itinerary__description"
                                            dangerouslySetInnerHTML={{ __html: text }}
                                        />
                                    )}
                                    {/* {text && <p className="itinerary__description">{text}</p>} */}
                                    <ul className="days-container">
                                        {itinerary &&
                                            itinerary.map(({ day, title, description, image }, index) => {
                                                return (
                                                    <DayDetail
                                                        key={index}
                                                        day={day}
                                                        title={title}
                                                        description={description}
                                                        image={image}
                                                    />
                                                )
                                            })}
                                    </ul>
                                </section>
                            )
                        })}
                    </article>
                ) : (
                    <section className="itinerary">
                        <ul className="days-container">
                            {itinerary.itinerary.map(({ day, title, description, image }, index) => {
                                return (
                                    <DayDetail
                                        key={index}
                                        day={day}
                                        title={title}
                                        description={description}
                                        image={image}
                                    />
                                )
                            })}
                        </ul>
                    </section>
                )}
            </div>

            <Reservations nameItinerary={itinerary.title} idItinerary={itinerary.id} />
            {itinerary.related_itineraries && <SimilarPackages related={itinerary.related_itineraries} />}

            <OperatorsGallery />
            {windowWidth < 992 && <ReservationButton />}
        </main>
    )
}

export default ItineraryDetail
